<template>
  <material-card
    v-resize="onResize"
    outlined
    class="fill-height d-flex barchart-root"
  >
    <chart-title :title="title" :unit="unitText(chartUnitPosition.TITLE)" :subtitle="subtitle" />
    <div class="legend-container">
      <div class="legend-item">
        <svg width="12" height="12">
          <rect width="100%" height="100%" :fill="getColor('default')" :rx="barRadius" :ry="barRadius" />
        </svg>
        <span>{{legendPrimary}}</span>
      </div>
      <div v-if="hasSecondaryValue" class="legend-item">
        <svg width="12" height="12">
          <rect width="100%" height="100%" fill="url(#pattern-default)" :rx="barRadius" :ry="barRadius"  />
        </svg>
        <span>{{legendSecondary}}</span>
      </div>
    </div>
    <div :id="id" class="graph-container">
      <svg
        v-if="initialized"
        :id="parentId"
        class="graph-svg"
      >
        <g :class="`${direction}-chart`">
          <g
            :id="`${id}-categories`"
            class="categories"
          />
          <g
            :id="`${id}-values`"
            class="values"
          />
        </g>
        <g>
          <defs>
            <!-- Hashed pattern -->
            <pattern
              v-for="status in [...chartStatus.toList(), 'default']"
              :id="`pattern-${status}`"
              :key="`pattern-${status}`"
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(140)"
            >
              <!-- White background -->
              <rect width="6" height="6" fill="white" />
              <!-- Hashed line -->
              <line x1="0" y1="0" x2="0" y2="6" :stroke="getColor(status, true)" stroke-width="4" />
            </pattern>
          </defs>
          <g 
            v-for="(bar, index) in data" :key="parentId + '-bar-group-' + index"
            @mouseenter="showPopover($event, index, bar)"
            @mouseleave="hidePopover">
            
            <!-- Filled bar: value -->
            <rect
              v-if="bar.value !== undefined && bar.value > 0"
              :x="isRow ? chartValues(roundedMin) : chartCategories(index)"
              :y="isRow ? chartCategories(index) : chartValues(bar.value)"
              :width="Math.max(0, isRow ? chartValues(bar.value) - chartValues(roundedMin) : chartCategories.bandwidth() / (hasSecondaryValue ? 2 : 1))"
              :height="Math.max(0, isRow ? chartCategories.bandwidth() / (hasSecondaryValue ? 2 : 1) : chartValues(roundedMin) - chartValues(bar.value))"
              :fill="getColor(bar.status)"
              :name="bar.name"
              :rx="barRadius" :ry="barRadius"
            />

            <!-- Hashed bar: secondaryValue -->
            <rect
              v-if="hasSecondaryValue && bar.value !== undefined && bar.secondaryValue > 0"
              :x="isRow ? chartValues(roundedMin) : chartCategories(index) + chartCategories.bandwidth() / 2"
              :y="isRow ? chartCategories(index) + chartCategories.bandwidth() / 2 : chartValues(bar.secondaryValue)"
              :width="Math.max(0, isRow ? chartValues(bar.secondaryValue) - chartValues(roundedMin) : chartCategories.bandwidth() / 2)"
              :height="Math.max(0, isRow ? chartCategories.bandwidth() / 2 : chartValues(roundedMin) - chartValues(bar.secondaryValue))"
              :fill="`url(#pattern-${bar.status || 'success'})`"
              :name="bar.name + '-secondary'"
              :rx="barRadius" :ry="barRadius"
            />
          </g>
        </g>
      </svg>
    </div>
    <rich-popover>
      <div class="richpopover-name"></div>
      <table class="data-container">
        <tr class="richpopover-primary">
          <td class="label text-body-1 color-text-secondary"></td>
          <td class="value"></td>
        </tr>
        <tr v-if="hasSecondaryValue" class="richpopover-secondary">
          <td class="label text-body-1 color-text-secondary"></td>
          <td class="value"></td>
        </tr>
      </table>
      <div class="richpopover-status">
        <div class="circle" style="width: 8px; height: 8px; border-radius: 50%;"></div>
        <span class="message text-body-1"></span>
      </div>
    </rich-popover>
  </material-card>
</template>

<script>
import * as d3 from 'd3'
import resize from 'vue-resize-directive'
import { chartDirection, chartStatus, chartUnitPosition } from '@/utils/enum'
import ChartTitle from '@/components/DashViews/Charts/ChartTitle.vue'
import RichPopover from '@/components/core/RichPopover.vue'

export default {
  name: 'BarChart',
  components: {
    ChartTitle,
    RichPopover,
  },
  directives: {
    resize,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: false,
      default: '',
    },
    legendPrimary: {
      type: String,
      required: false,
      default: '',
    },
    legendSecondary: {
      type: String,
      required: false,
      default: '',
    },
    data: {
      type: [Array],
      required: true,
    },
    categoryLabels: {
      type: Array,
      required: false,
      default: () => [],
    },
    unit: {
      type: String,
      required: false,
      default: '',
    },
    unitPosition: {
      type: String,
      required: false,
      default: '',
    },
    valueScale: {
      type: Number,
      required: false,
      default: 10,
    },
    direction: {
      type: String,
      required: false,
      default: chartDirection.COLUMN,
    },
    statusMessages: {
      type: Object,
      required: false,
      default: () => ({
        success: '',
        warning: '',
        error: '',
      }),
    }
  },
  data: function () {
    return {
      initialized: false,
      isCreated: false,
      width: null,
      height: null,
      chartValues: null,
      chartCategories: null,
      chartDirection,
      chartStatus,
      chartUnitPosition,
      barRadius: 2,
      barSpaceBetween: .2,
      barOuterMargin: 0.15,
      chartRow: {
        rightMargin: 16,
        bottomAxisHeight: 20,
        bottomAxisSpaceBetween: 12,
        leftAxisSpaceBetween: 16,
        leftAxisTickLineLength: 0,
        leftAxisWidth: 72
      },
      chartColumn: {
        topMargin: 8,
        rightMargin: 4,
        bottomAxisHeight: 28,
        bottomAxisSpaceBetween: 14,
        bottomAxisTickSize: 8,
        leftAxisSpaceBetween: 8, 
        leftAxisWidth: 48,
      },
    };
  },
  computed: {
    parentId() {
      return `${this.id}-svg`;
    },
    isRow() {
      return this.direction == chartDirection.ROW;
    },
    hasSecondaryValue() {
      return this.data.some(item => item.secondaryValue !== undefined && item.secondaryValue !== null);
    },
    roundedMin() {
      const padding = this.valueScale/2;
      const values = this.hasSecondaryValue 
        ? this.data.map(d => Math.min(d.value, d.secondaryValue)).filter(v => v > 0)
        : this.data.map(d => d.value).filter(v => v > 0);
      const minValue = (values.length > 0 ? d3.min(values) - padding : 0);
      return Math.floor(minValue / this.valueScale) * this.valueScale;
    },
    roundedMax() {
      const padding = this.valueScale/2;
      const values = this.hasSecondaryValue 
        ? this.data.map(d => Math.max(d.value, d.secondaryValue))
        : this.data.map(d => d.value);
      const maxValue = d3.max(values) + padding;
      return Math.ceil(maxValue / this.valueScale) * this.valueScale;
    },    
  },
  watch: {
    data() {
      this.onDataChange()
    },
    '$i18n.locale'() {
      if (this.isCreated) {
        this.renderAxes(); //ensures that the labels are updated when the locale changes
      }
    },
  },
  methods: {
    calcSize() {
      let parentBounding = d3.select(`#${this.id}`).node().getBoundingClientRect();
      this.width = parentBounding.width;
      this.height = parentBounding.height;

      if (this.isRow) {
        const valuesPositionTop = 0;
        const valuesPositionBottom = this.height - this.chartRow.bottomAxisHeight;
        this.chartCategories = d3
          .scaleBand()
          .domain(d3.range(this.data.length))
          .rangeRound([valuesPositionTop, valuesPositionBottom])
          .paddingInner(this.barSpaceBetween)
          .paddingOuter(this.barOuterMargin)

        const valuesPositionRight = this.width - this.chartRow.rightMargin;
        const valuesPositionLeft = this.chartRow.leftAxisWidth;
        this.chartValues = d3
          .scaleLinear()
          .domain([this.roundedMin, this.roundedMax])
          .rangeRound([valuesPositionLeft, valuesPositionRight]);
      }
      else {
        const valuesPositionRight = this.width - this.chartColumn.rightMargin;
        const valuesPositionLeft = this.chartColumn.leftAxisWidth;
        this.chartCategories = d3
          .scaleBand()
          .domain(d3.range(this.data.length))
          .rangeRound([valuesPositionLeft, valuesPositionRight])
          .paddingInner(this.barSpaceBetween)
          .paddingOuter(this.barOuterMargin)
        
        const valuesPositionTop = this.chartColumn.topMargin;
        const valuesPositionBottom = this.height - this.chartColumn.bottomAxisHeight;
        this.chartValues = d3
          .scaleLinear()
          .domain([this.roundedMin, this.roundedMax])
          .rangeRound([valuesPositionBottom, valuesPositionTop]);
      }

      this.initialized = true
    },
    renderAxes() {
      if (this.isRow) {
        this.rowLeftAxisDynamicWidth();
        d3.select(`#${this.id}-categories`)
          .attr('transform', `translate(${this.chartValues(this.roundedMin)}, 0)`)
          .call(
            d3.axisLeft(this.chartCategories)
              .tickFormat((i) => {
                return this.getEllipsisText(this.getCategoryLabel(i) || this.data[i].name);
              })
              .tickSize(this.chartRow.leftAxisTickLineLength)
              .tickPadding(this.chartRow.leftAxisSpaceBetween),
          )
          .call((g) => g.select('.domain').style('color', '#96A7B0')); //divider-dark
        this.isCreated = true;

        d3.select(`#${this.id}-values`)
          .call(
            d3.axisBottom(this.chartValues)
              .ticks(8)
              .tickFormat((d) => `${d}${this.unitText(chartUnitPosition.AXIS_LABEL)}`)
              .tickSizeInner(this.height - this.chartRow.bottomAxisHeight - 6)
              .tickPadding(this.chartRow.bottomAxisSpaceBetween),
          )
          .call((g) => {
            g.select('.domain').remove()
          });
      } else {
        d3.select(`#${this.id}-categories`)
          .attr('transform', `translate(0, ${this.height - this.chartColumn.bottomAxisHeight + 1})`)
          .call(
            d3.axisBottom(this.chartCategories)
              .tickFormat((i) => {
                const text = this.getCategoryLabel(i) || this.data[i].name;
                return text.length > 15 ? text.slice(0, 15) + '...' : text;
              })
              .tickSize(this.chartColumn.bottomAxisTickSize)
              .tickPadding(this.chartColumn.bottomAxisSpaceBetween - this.chartColumn.bottomAxisTickSize),
          )
          .call((g) => {
            const domain = g.select('.domain');
            domain.style('color', '#96A7B0'); //divider-dark
            this.removeEndsTicks(domain);
          });
        this.isCreated = true;

        d3.select(`#${this.id}-values`)
          .attr('transform', `translate(${this.width}, 0)`)
          .call(
            d3.axisLeft(this.chartValues)
              .ticks(8)
              .tickFormat((d) => `${d}${this.unitText(chartUnitPosition.AXIS_LABEL)}`)
              .tickSizeInner(this.width - this.chartColumn.leftAxisWidth)
              .tickPadding(this.chartColumn.leftAxisSpaceBetween),
          )
          .call((g) => {
            g.select('.domain').remove()
          });
      }
    },
    onResize() {
      this.calcSize();
      setTimeout(this.renderAxes, 0);
    },
    onDataChange() {
      if (this.isCreated) this.onResize();
    },
    getEllipsisText(text) {
      return text.length > 12 ? text.slice(0, 12) + '...' : text;
    },
    getColor(status = "success", isHashed = false) {
      const theme = this.$vuetify.theme.themes.light;
      const grey900 = "#202B34";
      const colorMap = {
        default: grey900,
        success: theme.success,
        warning: theme.warning,
        error: theme.error,
      };

      const color = colorMap[status] || grey900;
      return isHashed ? d3.color(color).darker(0.2).formatHex() : color;
    },
    unitText(position) {
      return this.unitPosition == position ? this.unit : '';
    },
    getCategoryLabel(dataIndex) {
      if (dataIndex === null || !this.data) return '';
      
      const valuesLength = this.data.length - 1;
      const label = this.categoryLabels[valuesLength - dataIndex];
      return label || '';
    },
    removeEndsTicks(domain) {
      const domainNode = domain.node();
      const domainPath = domainNode.getBBox();
      const domainElement = domainNode;
      const parent = domainElement.parentNode;
      
      // Add white rectangle at start
      const startRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      startRect.setAttribute('x', domainPath.x - 1);
      startRect.setAttribute('y', domainPath.y);
      startRect.setAttribute('width', '2');
      startRect.setAttribute('height', this.chartColumn.bottomAxisTickSize);
      startRect.setAttribute('fill', 'white');
      parent.insertBefore(startRect, domainElement.nextSibling);
        
      // Add white rectangle at end
      const endRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      endRect.setAttribute('x', domainPath.x + domainPath.width - 1);
      endRect.setAttribute('y', domainPath.y);
      endRect.setAttribute('width', '2');
      endRect.setAttribute('height', this.chartColumn.bottomAxisTickSize);
      endRect.setAttribute('fill', 'white');
      parent.insertBefore(endRect, startRect.nextSibling);
    },
    showPopover(event, index, barValues) {
      // Update the content
      const popover = d3.select('#rich-popover');
      const hasDecimal = (value) => {
        return Math.abs(value - Math.round(value)) >= 0.0001;
      }
      const format = hasDecimal(barValues.value) || (barValues.secondaryValue !== undefined && hasDecimal(barValues.secondaryValue)) ? ',.1f' : ',.0f';
      
      popover.style('display', 'block');
      popover.select('.richpopover-name').text(this.getCategoryLabel(index) || barValues.name);

      const popoverPrimary = popover.select('.richpopover-primary');
      popoverPrimary.select('.label').text(this.legendPrimary + ':');
      popoverPrimary.select('.value').text(d3.format(format)(barValues.value) + this.unit);

      if (barValues.secondaryValue !== undefined) {
        const popoverSecondary = popover.select('.richpopover-secondary');
        popoverSecondary.select('.label').text(this.legendSecondary + ':');
        popoverSecondary.select('.value').text(d3.format(format)(barValues.secondaryValue) + this.unit);
      }

      // Display status circle and message if the bar has a status
      const status = barValues.status || chartStatus.SUCCESS;
      const statusMessage = this.statusMessages[status];
      const popoverStatus = popover.select('.richpopover-status');
      if (statusMessage) {
        popoverStatus.style('display', 'flex');
        popoverStatus.select('.circle').style('background-color', this.getColor(status));
        popoverStatus.select('.message').text(statusMessage);
      } else {
        popoverStatus.style('display', 'none');
      }

      // Update the position
      const barPositions = event.currentTarget.getBoundingClientRect();
      const popoverHeight = popover.node().getBoundingClientRect().height;
      const popoverWidth = popover.node().getBoundingClientRect().width;
      const vMainStyle = window.getComputedStyle(document.querySelector('.v-main'));
      const screenWidth = window.innerWidth;
      const barTop = barPositions.top - parseFloat(vMainStyle.paddingTop);
      let barLeft = barPositions.left - parseFloat(vMainStyle.paddingLeft);

      if(this.isRow) {
        barLeft = barLeft + this.chartRow.leftAxisWidth;
        barLeft = this.preventOutOfScreen(barLeft, vMainStyle.paddingLeft, popoverWidth, screenWidth, false);
        popover
          .style('top', (barTop - popoverHeight) - 6 + 'px')
          .style('left', barLeft + 'px');
      }
      else {
        barLeft = barLeft + (barPositions.width / 2);
        barLeft = this.preventOutOfScreen(barLeft, vMainStyle.paddingLeft, popoverWidth, screenWidth, true);
        popover
          .style('top', (barTop - popoverHeight - 16) + 'px')
          .style('left', barLeft + 'px')
          .style('transform', 'translateX(-50%)');
      }
    },
    hidePopover() {
      d3.select('#rich-popover').style('display', 'none');
    },
    preventOutOfScreen(barLeft, paddingLeft, popoverWidth, screenWidth, isCentered) {
        const popOverRightEdge = parseFloat(paddingLeft) + barLeft + popoverWidth - (isCentered ? popoverWidth/2 : 0);
        const difference = popOverRightEdge - screenWidth;
        const padding = 8;
        return difference > -padding ? barLeft - difference - padding : barLeft;
    },
    rowLeftAxisDynamicWidth() {
      const higherLabelWidth = d3.max(this.data, (d, i) => {
        const label = this.getEllipsisText(this.getCategoryLabel(i) || d.name);
        const textNode = d3.select(`#${this.id}-categories`)
          .append('text')
          .style('opacity', 0)
          .text(label);
        const width = textNode.node().getBBox().width;
        textNode.remove();
        return width;
      });
      const maxLeftAxisWidth = higherLabelWidth + this.chartRow.leftAxisSpaceBetween;
      if (maxLeftAxisWidth > this.chartRow.leftAxisWidth) {
        this.chartRow.leftAxisWidth = maxLeftAxisWidth;
        this.calcSize();
      }
    }
  },
}
</script>

<style scoped lang="scss">
@import '@/styles/material-dashboard/variables.scss';

::v-deep(.v-card__text) {
  gap: $spacing-md;
  .barchart-root & {
    padding-bottom: 8px;
  }

  text{
    color: $text-secondary;
    font-family: $body-font-family;
    font-size: $font-size-12;
  }
}

::v-deep(.values) {
  line {
    opacity: 1;
    stroke-dasharray: 3 2;
    stroke: $divider-dark;
  }

  .tick:nth-child(even) {
    line {
      stroke: $divider-main;
    }
    text {
      display: none;
    }
  }
}

// Hide all bottom axis labels except first and last in column mode
::v-deep(.column-chart .categories) {
  .tick {
    text,
    line {
      display: none;
    }
  }
  .tick:first-of-type,
  .tick:last-child {
    text,
    line {
      display: block;
    }
  }
}

.barchart-root {
  width: 100%;
}

.richpopover-name {
  font-size: $font-size-18;
  line-height: 1em;
  margin-bottom: 4px;
}
.richpopover-primary,
.richpopover-secondary {
  line-height: 1.4em;

  .label {
    padding-right: 8px;
  }
  .value {
    font-size: $font-size-18;
    font-weight: 600;
    text-align: right;
  }
}
.richpopover-status {
  display: flex;
  align-items: center;
  line-height: 1em;
  margin-top: 10px;
  gap: 8px; 
}

.legend-container {
  display: flex;
  justify-content: flex-end;
  gap: 20px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    font-family: $body-font-family;
    font-size: $font-size-14;
    line-height: 1em;
    color: $text-primary;

    //fixes alignment
    display: inline-block;
    padding-top: 1px; 
  }
}

</style>


<!-- DOCUMENTATION --

BarChart.vue

  Description:
  This component renders a bar chart
  Props:
  - id: String                                        Id of the chart, used for d3
  - title: String                                     Title of the chart
  - subtitle: String          (Optional)              Subtitle used for units
  - legendPrimary: String     (Optional)              Primary legend text for solid bars
  - legendSecondary: String   (Optional)              Secondary legend text for hashed bars
  - data: Array                                       Data to display in the chart, format:
                                                      [{
                                                        name: String,
                                                        value: Number,          // Primary bar value
                                                        secondaryValue: Number, // Secondary bar value
                                                        status?: String         // Optional status for coloring (success, warning, error)
                                                      }]
  - categoryLabels: Array     (Optional)              Force the labels for the categories
  - unit: String              (Optional)              Unit of the data
  - unitPosition: String      (Optional)              Position of the unit (chartUnitPosition : title, axis-label)
  - valueScale: Number        (Optional)              Determines the rounding multiple for min and max values (default: 10)
  - direction: String         (Optional)              Direction of the chart (chartDirection : row or column)

  Features:
  - Dual bar display (solid and hashed) for comparing two values
  - Flexible orientation: column or row layout
  - Interactive hover effects:
    - Shows a detailed popover with both values
  - Status-based color coding (success, warning, error)

  -->